import React, { useMemo } from "react";
import type { EntityCustomViewParams } from "@rebasepro/cms-types";
import type { Property } from "@rebasepro/types";
import {
    Card as RebaseCard,
    Chip as RebaseChip,
    Typography as RebaseTypography,
    cls,
    defaultBorderMixin
} from "@rebasepro/ui";
import talentsCollection from "../collections/talents";
import { AREA_LABELS, LEVEL_LABELS, LEVEL_RANK, canonicalAreaKey, describeSalary, type AreaLevel } from "./_talent_taxonomy";

const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;

/**
 * The candidate's questionnaire, read the way it was answered.
 *
 * The form renders this map as what it structurally is: twenty-odd inputs in
 * declaration order, a screen you fill in rather than one you read. Nobody
 * fills it in here — the candidate did, on the public site — so what the team
 * actually needs from this tab is the opposite: the answers, grouped the way
 * the wizard asked them, with the empty ones out of the way instead of
 * occupying a row each.
 *
 * Everything on screen is derived from `collections/talents`, never restated:
 * the section labels are the properties' own `name`, and an enum value is
 * resolved through that property's `enum` list. Sixty-odd Spanish labels
 * copied into this file would be sixty-odd chances to drift from the wizard the
 * candidate saw, and the first symptom would be a recruiter reading a stale
 * word for a live answer.
 */

const QUESTIONNAIRE_KEY = "questionnaire";

/** The questionnaire's declared sub-properties, or an empty map. */
function questionnaireProperties(): Record<string, Property> {
    const q = (talentsCollection as any)?.properties?.[QUESTIONNAIRE_KEY];
    return (q?.properties ?? {}) as Record<string, Property>;
}

/**
 * The label an enum property gives a stored value.
 *
 * Falls back to the raw value: an answer written by an older version of the
 * wizard is still the candidate's answer, and showing it verbatim beats
 * dropping it because the current vocabulary no longer lists it.
 */
function enumLabel(property: Property | undefined, value: unknown): string {
    const raw = String(value);
    const list = (property as any)?.enum;
    if (Array.isArray(list)) {
        for (const entry of list) {
            if (entry && typeof entry === "object" && String((entry as any).id) === raw) {
                return String((entry as any).label ?? raw);
            }
        }
    }
    return raw;
}

/** Declared name of a questionnaire field, falling back to its key. */
function fieldName(key: string): string {
    const property = questionnaireProperties()[key] as any;
    return property?.name ?? key;
}

const isBlank = (v: unknown): boolean =>
    v === null || v === undefined || v === "" ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === "object" && !Array.isArray(v) && Object.keys(v as object).length === 0);

/** A labelled line. Renders nothing at all when the answer is blank. */
function Line({ label, children }: { label: string; children?: React.ReactNode }) {
    if (children === null || children === undefined || children === false) return null;
    return (
        <div className="flex flex-col gap-0.5 min-w-0">
            <Typography variant="body2" color="secondary">{label}</Typography>
            <div className="text-sm">{children}</div>
        </div>
    );
}

/** A questionnaire field rendered as text, resolving its enum if it has one. */
function TextLine({ values, field }: { values: Record<string, any>; field: string }) {
    const value = values[field];
    if (isBlank(value)) return null;
    const property = questionnaireProperties()[field];
    return <Line label={fieldName(field)}>{enumLabel(property, value)}</Line>;
}

/** A string-array field as chips — certifications, degrees, work models. */
function ChipLine({ values, field }: { values: Record<string, any>; field: string }) {
    const value = values[field];
    if (!Array.isArray(value) || value.length === 0) return null;
    const of = (questionnaireProperties()[field] as any)?.of;
    return (
        <Line label={fieldName(field)}>
            <div className="flex flex-wrap gap-1 mt-0.5">
                {value.map((v: unknown, i: number) => (
                    <Chip key={`${String(v)}-${i}`} size="small" colorScheme="grayLighter">
                        {enumLabel(of, v)}
                    </Chip>
                ))}
            </div>
        </Line>
    );
}

/** A section, rendered only when it has at least one answered field in it. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
    const filled = React.Children.toArray(children).some(Boolean);
    if (!filled) return null;
    return (
        <Card className={cls("p-4 flex flex-col gap-3", defaultBorderMixin)}>
            <Typography variant="subtitle2" component="h3" className="uppercase tracking-wide opacity-70">
                {title}
            </Typography>
            <div className="grid gap-x-6 gap-y-3 grid-cols-1 sm:grid-cols-2">
                {children}
            </div>
        </Card>
    );
}

/**
 * The ESG areas the candidate graded, strongest first.
 *
 * Sorted rather than listed in declaration order because the question this
 * answers is "what is this person actually good at" — and an alphabetical list
 * with `avanzado` in nineteenth place does not answer it. Areas graded `none`
 * are dropped: the wizard records them, and they are the absence of a claim.
 *
 * Keys the map does not declare are kept and humanised. An older questionnaire
 * wrote English ones, they are still in live rows, and dropping them would hide
 * a real answer — the same reasoning as `SustainabilityAreasField`.
 */
function AreasSection({ areas, otherText }: { areas: Record<string, unknown>; otherText?: string }) {
    const graded = useMemo(() => {
        return Object.entries(areas)
            .map(([key, level]) => ({
                key: canonicalAreaKey(key),
                level: String(level) as AreaLevel,
                label: AREA_LABELS[canonicalAreaKey(key)]
                    ?? key.replace(/[_-]+/g, " ").replace(/^./, c => c.toUpperCase())
            }))
            .filter(a => a.level && a.level !== "none")
            .sort((a, b) => (LEVEL_RANK[b.level] ?? 0) - (LEVEL_RANK[a.level] ?? 0));
    }, [areas]);

    if (graded.length === 0 && !otherText) return null;

    return (
        <Card className={cls("p-4 flex flex-col gap-3", defaultBorderMixin)}>
            <Typography variant="subtitle2" component="h3" className="uppercase tracking-wide opacity-70">
                Áreas de sostenibilidad
            </Typography>
            <div className="flex flex-col gap-2">
                {graded.map(area => (
                    <div key={area.key} className="flex items-baseline justify-between gap-3 min-w-0">
                        <span className="text-sm truncate">{area.label}</span>
                        <Chip size="small" colorScheme="grayLighter" className="shrink-0">
                            {LEVEL_LABELS[area.level] ?? area.level}
                        </Chip>
                    </div>
                ))}
            </div>
            {otherText && (
                <Line label={fieldName("sustainability_areas_other_text")}>{otherText}</Line>
            )}
        </Card>
    );
}

/**
 * How much of the questionnaire the candidate actually filled in.
 *
 * Stated up front because it changes how the rest of the tab should be read: a
 * profile answering six of twenty fields is not a weak candidate, it is an
 * incomplete form, and the difference matters before anybody concludes anything
 * from an empty "certificaciones".
 */
function Completeness({ values }: { values: Record<string, any> }) {
    const declared = Object.keys(questionnaireProperties());
    const answered = declared.filter(k => !isBlank(values[k]));
    if (declared.length === 0) return null;
    const pct = Math.round((answered.length / declared.length) * 100);
    return (
        <Typography variant="body2" color="secondary">
            {/* The bare percentage beside a person's name read as a score of
                the person, which is the one thing this line must not say. */}
            Contestó {answered.length} de las {declared.length} preguntas
        </Typography>
    );
}

export function QuestionnairePanel({ entity }: EntityCustomViewParams) {
    const values = (entity?.values ?? {}) as Record<string, any>;
    const q = (values[QUESTIONNAIRE_KEY] ?? {}) as Record<string, any>;

    const areas = (q.sustainability_areas ?? {}) as Record<string, unknown>;
    const otherText = typeof q.sustainability_areas_other_text === "string"
        ? q.sustainability_areas_other_text
        : undefined;

    if (isBlank(q)) {
        return (
            <div className="p-6">
                <Typography variant="body1" color="secondary">
                    Esta persona todavía no ha respondido el cuestionario.
                </Typography>
            </div>
        );
    }

    // Never the bare number: until the form asked for a period, the same
    // figure meant a month for one candidate and a year for the next. See
    // `describeSalary`.
    const salary = describeSalary(q);

    return (
        <div className="p-4 flex flex-col gap-4 overflow-auto">
            <div className="flex items-baseline justify-between gap-3">
                <Typography variant="h6">Cuestionario profesional</Typography>
                <Completeness values={q}/>
            </div>

            <Section title="Formación">
                <ChipLine values={q} field="degree"/>
                <ChipLine values={q} field="other_studies"/>
                <ChipLine values={q} field="certifications"/>
                <ChipLine values={q} field="skills"/>
            </Section>

            <Section title="Experiencia">
                <TextLine values={q} field="years_experience_total"/>
                <TextLine values={q} field="years_experience_sustainability"/>
                <TextLine values={q} field="sustainability_start_year"/>
                {/* Languages are `{language, level}` pairs, so neither TextLine
                    nor ChipLine can render them without inventing a shape. */}
                {Array.isArray(q.languages) && q.languages.length > 0 && (
                    <Line label={fieldName("languages")}>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                            {q.languages.map((l: any, i: number) => {
                                const levelProp = (questionnaireProperties().languages as any)
                                    ?.of?.properties?.level;
                                const name = l?.language ?? "—";
                                const level = l?.level ? enumLabel(levelProp, l.level) : null;
                                return (
                                    <Chip key={`${name}-${i}`} size="small" colorScheme="grayLighter">
                                        {level ? `${name} · ${level}` : name}
                                    </Chip>
                                );
                            })}
                        </div>
                    </Line>
                )}
            </Section>

            <AreasSection areas={areas} otherText={otherText}/>

            <Section title="Ubicación y movilidad">
                <TextLine values={q} field="country"/>
                <TextLine values={q} field="city"/>
                <ChipLine values={q} field="preferred_work_model"/>
                <TextLine values={q} field="travel_availability"/>
                <TextLine values={q} field="relocation_available"/>
                <TextLine values={q} field="relocation_conditions"/>
            </Section>

            <Section title="Preferencias">
                {/*
                  The label used to say "(bruto anual)" over a number that was
                  monthly for half the base. The period now lives in the figure
                  itself ("MXN/mes"), and where it had to be worked out rather
                  than answered, the note underneath says so.
                */}
                {salary && (
                    <Line label="Expectativa salarial">
                        <span className={salary.source === "unresolved" ? "opacity-70" : undefined}>{salary.text}</span>
                        {salary.note && (
                            <Typography variant="caption" color="secondary" className="block mt-0.5">
                                {salary.source === "inferred" ? "≈ " : ""}{salary.note}
                            </Typography>
                        )}
                    </Line>
                )}
                <ChipLine values={q} field="preferred_job_functions"/>
                <ChipLine values={q} field="preferred_org_type"/>
            </Section>

            {/* Full width and last: it is prose the candidate wrote, and the
                only thing here worth reading slowly. */}
            {!isBlank(q.career_motivation) && (
                <Card className={cls("p-4 flex flex-col gap-2", defaultBorderMixin)}>
                    <Typography variant="subtitle2" component="h3" className="uppercase tracking-wide opacity-70">
                        {fieldName("career_motivation")}
                    </Typography>
                    <Typography variant="body1" className="whitespace-pre-wrap">
                        {String(q.career_motivation)}
                    </Typography>
                </Card>
            )}
        </div>
    );
}

export default QuestionnairePanel;
